/**
 * TASK-TMS-002: Default Sci-Fi Theme Configuration
 *
 * Cyber Arena theme - A futuristic digital battlefield with neon cyan accents
 * and dark navy ambiance. This is the default theme for battles.
 */

import type { TerrainTheme } from '../../types/terrain';

/**
 * Cyber Arena theme configuration
 *
 * Visual Design:
 * - Color Palette: Cyan (0x00D4FF) grid glow, deep navy (0x0A1628) background
 * - Parallax: Three-layer background with subtle depth (0.1, 0.3, 0.5 factors)
 * - Particles: Slow-drifting fog and floating dust with horizontal sway
 * - Grid: Pulsing Tron-style glow effects with materialization animations
 */
export const CYBER_ARENA_THEME: TerrainTheme = {
  themeId: 'cyber-arena',
  name: 'Cyber Arena',
  description: 'A futuristic digital battlefield with neon accents',
  thumbnail: '/assets/themes/cyber-arena/thumbnail.png',

  // Asset URLs
  assets: {
    farBackground: '/assets/themes/cyber-arena/far-bg.png', // Uses placeholder
    midBackground: '/assets/themes/cyber-arena/mid-bg.png', // Uses placeholder
    nearBackground: '/assets/themes/cyber-arena/battlefieldimages/Generated Image February 07, 2026 - 2_32PM.jpeg', // Castle courtyard with torches
    fogParticle: '/assets/themes/cyber-arena/fog-particle.png', // Uses placeholder
    dustParticle: '/assets/themes/cyber-arena/dust-particle.png', // Uses placeholder
  },

  // Color palette
  colors: {
    gridGlow: 0x00d4ff, // Cyan - primary accent color
    fogTint: 0x1a3a5c, // Blue-gray - subtle fog color
    dustTint: 0x00ffff, // Bright cyan - dust particles
    ambientLight: 0x0a1628, // Deep navy - overall scene tint
  },

  // Parallax background settings
  // Far layer moves slowest (10%), near layer moves fastest (50%)
  parallax: {
    far: { factor: 0.1, scale: 1.0 },
    mid: { factor: 0.3, scale: 1.0 },
    near: { factor: 0.5, scale: 1.0 },
  },

  // Particle system settings
  particles: {
    // Ground fog - slow horizontal drift, wraps at edges
    fog: {
      density: 50, // ~40-50 particles for typical battlefield size
      speed: { min: 5, max: 15 }, // Pixels per second
      scale: { min: 0.5, max: 2.0 }, // Size variation
      alpha: { min: 0.15, max: 0.5 }, // Subtle but visible opacity
    },
    // Floating dust - upward drift with sway, fades at top
    dust: {
      density: 20, // ~15-20 particles for typical battlefield size
      speed: { min: 10, max: 30 }, // Pixels per second
      scale: { min: 0.3, max: 0.6 }, // Small particles (slightly larger)
      alpha: { min: 0.4, max: 0.9 }, // More visible than fog
      swayAmplitude: 20, // Pixels of horizontal sway
      swayFrequency: 0.5, // Oscillations per second
    },
  },

  // Tron-style grid effects
  gridEffects: {
    glowIntensity: 0.8, // Strong glow (0-1)
    glowPulseSpeed: 2.0, // Moderate pulse speed
    glowPulseAmount: 0.2, // Subtle intensity variation
    materializeSpeed: 400, // Hex fade-in duration (ms)
    spawnEffectDuration: 600, // Creature spawn effect (ms)
  },
};
